import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './index.module.scss';
import classNames from 'classnames/bind';
import PageLayout from 'layout/page';
import Icon from 'components/icon';
import Card from 'components/card';
import Button from 'components/button';
import ButtonDialog from 'components/button-dialog';
import iconSetting from 'assets/icon_action_white.svg';
import iconCheck from 'assets/icon_check.svg';
import defaultCard from 'assets/card.jpg';
import iconFail from 'assets/icon_alert_red.svg';
import lineBankLogo from 'assets/line-bank_logo.png';
import addCardMochiBank from 'lottie/addcard_mochibank.json';
import Lottie from 'react-lottie';
import amplitude from 'service/amplitude';
import {
  useMochiPayFlowContext,
  INITIALIZE,
  CHANGE_SETTING_DIALOG_SHOW,
  CHANGE_REMOVE_DIALOG_SHOW,
} from 'feature/mochi-pay-flow/use-mochi-pay-flow';
import { PATH } from 'config';
import { SHOW_TOAST_MESSAGE, useAppContext } from 'use-app';
import get from 'lodash/get';
import { getLoginState } from 'model/user';
import { openLink } from 'helpers/link-helper';
import { isLineBank } from 'config';

const cx = classNames.bind(styles);

const CreditCard = () => {
  const { state, dispatch } = useMochiPayFlowContext();
  const card = state.currentCreditCard;

  const divStyle = () => {
    const { creditCardCategory } = card;
    if (creditCardCategory && creditCardCategory.imageUrl) {
      return {
        backgroundImage: `url('${creditCardCategory.imageUrl}')`,
      };
    } else {
      if (isLineBank) {
        return {};
      } else {
        return {
          backgroundImage: `url('${defaultCard}')`,
        };
      }
    }
  };

  const onSettingClick = () => {
    if (isLineBank) return;
    dispatch({ type: CHANGE_SETTING_DIALOG_SHOW, payload: true });
  };

  const BankName = () => {
    if (isLineBank) return <img src={lineBankLogo} alt="logo" />;
    return card.bank?.name || '您的銀行卡號';
  };

  const cardClass = cx({
    'credit-card': true,
    linebank: isLineBank,
  });

  const color = () => {
    if (isLineBank) return 'black';
    return card.creditCardCategory?.textColor || 'white';
  };

  return (
    <div className={cardClass} style={divStyle()} onClick={onSettingClick}>
      <div className={styles['card-content']} style={{ color: color() }}>
        <div className={styles['bank']}>
          <BankName />
        </div>
        <div className={styles['last-four-code']}>
          <span>···· ···· ···· </span>
          {card.lastFourCode}
        </div>
      </div>
      <Icon className={styles.setting} imgSrc={iconSetting} height="18px" alt="iconSetting" />
    </div>
  );
};

const EmptyCreditCard = () => {
  const { dispatch: appDispatch } = useAppContext();
  const history = useHistory();

  const addCreditCard = () => {
    if (isLineBank) return;

    if (getLoginState() !== 'user') {
      appDispatch({
        type: SHOW_TOAST_MESSAGE,
        payload: '請先登入再繼續',
      });
      history.push(PATH.USER_LOGIN);
    } else {
      amplitude.payment_addCard_onClick();
      history.push(PATH.MOCHIPAY_FLOW_CREDIT_CARD);
    }
  };

  return (
    <Card>
      <div className={styles['empty-credit-card']} onClick={addCreditCard}>
        <Lottie
          options={{
            autoplay: true,
            loop: true,
            animationData: addCardMochiBank,
          }}
        />
        <div className={styles['add-payment']}>
          <Button theme="link" block={true}>
            新增付款方式
          </Button>
        </div>
      </div>
    </Card>
  );
};

const CreditCardDisplay = () => {
  const { state } = useMochiPayFlowContext();

  if (state.currentCreditCard === null) {
    return <EmptyCreditCard />;
  } else {
    return <CreditCard />;
  }
};

const Hint = () => {
  const { state } = useMochiPayFlowContext();

  const Content = () => {
    if (!state.currentCreditCard) return <></>;
    if (state.theme === 'last-payment-fail') {
      return (
        <>
          <img src={iconFail} alt="icon-fail" />
          前一筆交易卡片授權失敗，請洽詢發卡行或重新設定。
        </>
      );
    }
    return (
      <>
        <img src={iconCheck} alt="icon-check" />
        付款方式已設定，歡迎使用。
      </>
    );
  };
  return (
    <div className={styles['hint']}>
      <Content />
    </div>
  );
};

const Promotion = () => {
  const { state } = useAppContext();
  const promotionBoard = get(state, 'promotionBoard');
  const card = useRef();

  if (promotionBoard) {
    return promotionBoard.map(x => {
      return (
        <div
          className={styles['promotion-card']}
          ref={card}
          style={{
            backgroundImage: `url(${x.attributes.imageUrl})`,
            backgroundColor: x.attributes.backgroundColor,
            minHeight: get(card, 'current.clientWidth') * 0.4,
          }}
          onClick={() => openLink(x.attributes.url)}
        >
          <div className={styles.text}>
            <div
              className={styles.title}
              style={{
                color: x.attributes.titleColor,
              }}
            >
              {x.attributes.title}
            </div>
            <div
              className={styles.desc}
              style={{
                color: x.attributes.subtitleColor,
              }}
            >
              {x.attributes.subtitle}
            </div>
          </div>
        </div>
      );
    });
  } else {
    return <></>;
  }
};

const SettingDialog = () => {
  const { state, dispatch } = useMochiPayFlowContext();
  const history = useHistory();

  const resetWallet = () => {
    amplitude.payment_setting_reset_onClick();
    dispatch({ type: CHANGE_SETTING_DIALOG_SHOW, payload: false });
    history.push(PATH.MOCHIPAY_FLOW_CREDIT_CARD);
  };
  const showRemoveDialog = () => {
    amplitude.payment_setting_delete_onClick();
    closeSettingDialog();
    dispatch({ type: CHANGE_REMOVE_DIALOG_SHOW, payload: true });
  };
  const closeSettingDialog = () => {
    amplitude.payment_setting_cancel_onClick();
    dispatch({ type: CHANGE_SETTING_DIALOG_SHOW, payload: false });
  };

  return (
    <ButtonDialog
      title="選擇動作"
      items={[
        {
          content: '重新設定付款卡片',
          theme: 'positive',
          onClick: resetWallet,
          beLoading: true,
        },
        {
          content: '刪除目前設定',
          theme: 'negative',
          onClick: showRemoveDialog,
        },
        {
          content: '取消',
          theme: 'cancel',
          onClick: closeSettingDialog,
        },
      ]}
      isShow={state.settingDialogShow}
    />
  );
};

const RemoveDialog = () => {
  const { state, dispatch, removeWallet } = useMochiPayFlowContext();
  const closeMochiPay = () => {
    amplitude.payment_setting_delete_confirm_onClick();
    removeWallet();
  };
  const closeDialog = () => {
    amplitude.payment_setting_delete_cancel_onClick();
    dispatch({ type: CHANGE_REMOVE_DIALOG_SHOW, payload: false });
  };

  return (
    <ButtonDialog
      title="確定刪除付款設定?"
      horizonal
      items={[
        {
          content: '確定刪除',
          theme: 'positive',
          onClick: closeMochiPay,
          beLoading: true,
        },
        {
          content: '取消',
          theme: 'cancel',
          onClick: closeDialog,
        },
      ]}
      isShow={state.removeDialogShow}
    />
  );
};

const MochiPaySetting = () => {
  const { state, dispatch } = useMochiPayFlowContext();
  useEffect(() => {
    dispatch({ type: INITIALIZE });
  }, [dispatch]);

  const itemClasses = cx({
    'mochi-pay-setting': true,
    [state.theme]: true,
  });

  return (
    <PageLayout>
      <div className={itemClasses}>
        <div className={styles['body']}>
          <CreditCardDisplay />
          <Hint />
          <div className={styles['promotion']}>
            <Promotion />
          </div>
        </div>
        <SettingDialog />
        <RemoveDialog />
      </div>
    </PageLayout>
  );
};

export default MochiPaySetting;
